import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Loader, Progress, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconUpload } from '@tabler/icons-react';
import classes from './UploadZone.module.css';

interface UploadZoneProps {
  readonly accept?: string;
  readonly multiple?: boolean;
  readonly loading?: boolean;
  /** Процент отправленных байт (0..100) во время загрузки; undefined — прогресс неизвестен. */
  readonly percent?: number;
  /** Подсказка форматов, напр. «PDF, SIG». */
  readonly hint?: string;
  /** Максимальный размер файла в МБ — добавляется в подсказку после форматов. */
  readonly maxSizeMb?: number;
  /** Ошибка валидации/загрузки под зоной (уже локализованная строка). */
  readonly error?: string;
  readonly onFiles: (files: File[]) => void;
}

// Дропзона загрузки в стиле сайта: клик + drag&drop. Понятная неопытному пользователю
// (явная рамка + иконка + текст). Во время отправки на сервер — неактивна, с прогресс-баром.
export default function UploadZone({ accept, multiple, loading, percent, hint, maxSizeMb, error, onFiles }: UploadZoneProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);

  // Байты ещё идут → детерминированный бар; ушли все (100) или прогресс неизвестен → «завершается».
  const transferring = loading && percent !== undefined && percent < 100;

  function emit(list: FileList | null): void {
    if (!list || list.length === 0) {
      return;
    }
    const files = Array.from(list);
    onFiles(multiple ? files : files.slice(0, 1));
  }

  function open(): void {
    inputRef.current?.click();
  }

  return (
    <Stack gap={6}>
      <div
        className={classes.zone}
        data-dragging={dragging || undefined}
        data-loading={loading || undefined}
        data-error={error && !loading ? true : undefined}
        role="button"
        tabIndex={0}
        aria-label={t('app.company.accreditation.uploadTitle')}
        aria-busy={loading || undefined}
        onClick={open}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          event.preventDefault();
          dragDepth.current -= 1;
          if (dragDepth.current <= 0) {
            dragDepth.current = 0;
            setDragging(false);
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          dragDepth.current = 0;
          setDragging(false);
          emit(event.dataTransfer.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          hidden
          onChange={(event) => {
            emit(event.currentTarget.files);
            event.currentTarget.value = ''; // повторный выбор того же файла снова триггерит change
          }}
        />
        <Group gap="sm" justify="center" wrap="nowrap">
          {loading ? <Loader size="sm" /> : <IconUpload size={22} className={classes.icon} />}
          <Stack gap={2}>
            <Text fw={500} size="sm">
              {loading
                ? transferring
                  ? t('app.company.accreditation.uploading', { percent })
                  : t('app.company.accreditation.processing')
                : t('app.company.accreditation.uploadTitle')}
            </Text>
            {!loading ? (
              <Text size="xs" c="gray.6">
                {t('app.company.accreditation.uploadHint')}
                {hint ? ` · ${hint}` : ''}
                {maxSizeMb ? ` · ${t('app.company.accreditation.maxSize', { mb: maxSizeMb })}` : ''}
              </Text>
            ) : null}
          </Stack>
        </Group>

        {loading ? (
          <Progress
            className={classes.progress}
            value={transferring ? percent : 100}
            animated={!transferring}
            size="sm"
            radius={0}
            transitionDuration={150}
          />
        ) : null}
      </div>

      {error && !loading ? (
        // role="alert": ошибка появляется после выбора файла, и программа экранного доступа должна её объявить.
        <Group gap={6} wrap="nowrap" c="red.7" role="alert">
          <IconAlertCircle size={16} style={{ flexShrink: 0 }} />
          <Text size="xs" c="red.7">
            {error}
          </Text>
        </Group>
      ) : null}
    </Stack>
  );
}
